import size from 'lodash/size';
import omitBy from 'lodash/omitBy';
import template from 'string-placeholder';
import keys from 'lodash/keys';
import pick from 'lodash/pick';
import merge from 'lodash/merge';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import find from 'lodash/find';
import entries from 'lodash/entries';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';
import stripTags from 'striptags';
import { compareKeys, getOrFail } from 'mythil/object';
import map from 'lodash/map';
import values from 'lodash/values';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import forOwn from 'lodash/forOwn';
import reduce from 'lodash/reduce';
import filter from 'lodash/filter';
import identify from '~/identify';
import normalizeFieldValue from '~/data/normalizeFieldValue';
import { arrayed } from 'mythil/array';
import replaceall from 'replaceall';
import { deFormatDate, deFormatDateTime, parseGermanDate } from 'mythil/time';
import {
    getValueAdaptData,
    getValueAdaptSources,
    getAllResultParams,
    getFilters,
    getSelectedRows,
    getSelectThroughout,
    getRouter,
    getModelNameOrNull,
    getActiveRelationshipField,
    getCurrentModelId,
    getModelTree,
    getSubviewCount,
    isDataModelLoaded,
    getDataActionPermissions,
    getActionPermissions,
    getFieldGrid,
} from '~/stateGetter';
import getConfig from '~/config';
import { fieldFile, timthumb } from '~/backend/backendUrl';

import { decode } from 'js-base64/base64';
import { getFilterType, isDateFilter } from './lib/filterTypes';

export const isFieldValueEqual = (v1, v2) => {
    return isEqual(v1.value, v2.value) && v2.min === v2.min && v2.max === v2.max;
};

// TODO: visibleconditions
export function presentFilters({ groupName, exceptGroupName, filters, parentSubview }) {
    let slice = filter(filters, (f) => {
        if (!f.visible || !f.editable) {
            return false;
        }
        if (groupName && f.group !== groupName) {
            return false;
        }
        if (exceptGroupName && f.group === groupName) {
            return false;
        }
        if (parentSubview && parentSubview.hiddenFields.indexOf(f.name) > -1) {
            return false;
        }

        return true;
    });

    if (groupName) {
        slice = filter(slice, (f) => f.group === groupName);
    }

    if (exceptGroupName) {
        slice = filter(slice, (f) => f.group !== exceptGroupName);
    }

    return orderBy(values(slice), 'order');
}

export function presentGroup(props) {
    let { group, groupOpen, filters } = props,
        presentedGroup = { ...group, open: groupOpen },
        groupName = presentedGroup.name;

    if (filters) {
        presentedGroup.filters = presentFilters({ groupName, ...props });
    }

    return presentedGroup;
}

export function presentGroups(props) {
    let { groupStates, groups, filters, onlyOpen } = props;

    filters = presentFilters(props);

    const filtersByGroups = groupBy(filters, 'group');
    return filters.reduce((filterGroups, filter) => {
        const defaultOpen = filter.group === 'default';
        const groupOpen = get(groupStates, filter.group);

        if (typeof groupOpen === 'boolean') {
            groupStates[filter.group] = filtersByGroups[filter.group].reduce(
                (state, filter) => Object.assign(state, { [filter.name]: groupOpen }),
                {}
            );
        }

        const filterOpen = get(groupOpen, filter.name, defaultOpen);
        // if(onlyOpen && !filterOpen) { return filterGroups; }

        filterGroups[filter.group] = filterGroups[filter.group] || {
            name: filter.group,
            title: filter.group,
            ...groups[filter.group],
            filters: [],
            open: Boolean(groupOpen),
        };

        filterGroups[filter.group].filters.push({ ...filter, open: filterOpen });

        return filterGroups;
    }, {});
}

export function normalizeFilterValue({ value, min, max }) {
    if (min) {
        return { min, max };
    }
    return value;
}

export function presentFilterValues({ filterValues, ...other }) {
    let subviewFilters = presentSubviewFilterValues(other);

    return {
        ...filterValues,
        ...subviewFilters,
    };
}

/**
 *
 * @param groupStates if specified only filterValues from open groups will be returned
 * @param filters filter definitions
 * @param filterValues all filter values
 * @param initialFilterValues if specified only filterValues that do not equal the default will be returned
 * @param parentItemValues for getting subview filter values
 * @param parentSubview for getting subview filter values
 * @returns {*}
 */

export function presentAppliedFilterOrder(props) {
    let { filterValues, filterOrder } = props;

    const asOrderedArr = (arr) =>
        orderBy(
            arr.map(([name, { created_at }]) => ({
                name,
                created_at: created_at ?? 0,
                order: get(filterOrder, name) ?? 0,
            })),
            ['created_at', 'order'],
            ['desc', 'desc']
        );

    return [
        ...asOrderedArr(entries(filterValues).filter(([_, { created_at }]) => created_at)),
        ...asOrderedArr(entries(filterValues).filter(([_, { created_at }]) => !created_at)),
    ].map(({ name }) => name);
}

const fieldHasValue = ({ value, filterType }) => {
    const __isEmpty = (value) => isNil(value) || value === '';
    if (isDateFilter(filterType)) {
        return true;
    }

    if (__isEmpty(value)) {
        return false;
    }

    if (Array.isArray(value)) {
        return !!value.length;
    }

    if (typeof value === 'object') {
        const isMinMax = 'min' in value || max in value;
        if (isMinMax && __isEmpty(value.max) && __isEmpty(value.min)) {
            return false;
        }
    }

    return true;
};

export function presentAppliedFilterValues(props) {
    let { filters, filterValues, initialFilterValues, modelSearchFilter, takeAllCreatedFilters } = props;

    let appliedFilters = reduce(
        filters,
        (valCollector, filter, filterName) => {
            if (filterName === modelSearchFilter) {
                return valCollector;
            }

            let defaultFilterValue = get(initialFilterValues, filterName, undefined);
            let filterValue = get(filterValues, filterName, {});

            let takeValue = fieldHasValue(filterValue) || (!!takeAllCreatedFilters && !!filterValue.created_at);

            if (typeof defaultFilterValue === 'undefined' || takeValue) {
                valCollector[filterName] = filterValue.value;
            }

            return valCollector;
        },
        {}
    );

    let subviewFilters = mapValues(presentSubviewFilterValues(props), normalizeFilterValue);

    return { ...appliedFilters, ...subviewFilters };
}

export function presentAppliedFilterValuesObject(props) {
    const appliedFilters = presentAppliedFilterValues(props);
    return mapValues(appliedFilters, (value, filterName) => {
        const filter = get(props.filterValues, filterName);
        return {
            filterType: filter?.filterType,
            created_at: filter?.created_at ?? new Date(filter?.created_at).getTime(),
            value,
        };
    });
}

export function canColumnBeVisible(columnName, { columns, parentSubview }) {
    let { visible, name, columnVisible } = columns[columnName];

    if (!visible || !columnVisible) {
        return false;
    }

    if (parentSubview) {
        if (parentSubview.hiddenColumns.indexOf(name) > -1) {
            return false;
        }
    }

    return true;
}

export function isColumnHidden(columnName, { columns, parentSubview, view, columnVisibility = null }) {
    if (columnVisibility && columnName in columnVisibility) {
        return !columnVisibility[columnName];
    }

    if (parentSubview && parentSubview.visibleColumns.length && (view === identify.VIEW_SUBVIEW || isItemView(view))) {
        return parentSubview.visibleColumns.indexOf(columnName) < 0;
    }

    return columns[columnName].hidden;
}

export function presentSortOptions(props) {
    let { sortOptions, isCalendarEnabled, calendarView } = props;

    if (isCalendarEnabled && calendarView) {
        return {
            direction: 'asc',
            field: calendarView.from_column,
        };
    }

    return sortOptions;
}

export function presentColumns(props) {
    let { columns, columnOrder, isCalendarEnabled, calendarView } = props;

    if (isCalendarEnabled && calendarView) {
        return [{ name: calendarView.from_column }, { name: calendarView.to_column }];
    }

    columns = values(columns).reduce((currentColumns, column) => {
        let { name } = column;
        if (!canColumnBeVisible(name, props)) {
            return currentColumns;
        }

        currentColumns.push({
            ...column,

            // offset default index by 1 to allow moving columns to first position.
            displayOrder: +get(columnOrder, column.name, currentColumns.length + 1),
            tooltip: props.tooltipsActive ? column.tooltip : null,
            hidden: isColumnHidden(name, props),
        });

        return currentColumns;
    }, []);

    return sortBy(columns, (column) => column.displayOrder);
}

export function presentFieldOption(field, value, autoCompleteOptions) {
    let options = field.options;

    if (field.isAutocomplete) {
        options = get(autoCompleteOptions, `${field.name}.autocomplete.value`, []);
    }

    let option = find(options, { value });

    return option ? option : {};
}

export function presentConditionSources(visibilityConditions) {
    let valueKeys = [],
        optionKeys = [];

    forOwn(visibilityConditions, (condition, field) => {
        if (!isString(condition)) {
            return;
        }

        valueKeys.push(field);
        optionKeys.push(field);

        let regex = /\.(.*?)(\s|$|\))/g,
            match;

        do {
            match = regex.exec(condition);
            if (match) {
                valueKeys.push(match[1]);
            }
        } while (match);
    });

    return {
        valueKeys: valueKeys,
        optionKeys: optionKeys,
    };
}

export function presentFieldConditionSources(fields) {
    let valueKeys = [],
        optionKeys = [];

    const prepareSources = (prepareConditions) => forOwn(fields, (field) => {
        let conditionSources = presentConditionSources(prepareConditions(field));

        valueKeys = [...valueKeys, ...conditionSources.valueKeys];
        optionKeys = [...optionKeys, ...conditionSources.optionKeys];
    });

    prepareSources((field) => field.visibleConditions)
    prepareSources((field) => field.disableConditions)

    return {
        valueKeys: uniq(valueKeys),
        optionKeys: uniq(optionKeys),
    };
}

export function presentPrimaryKey({ values, primaryKeyAttr = 'id', defaultKey = null }) {
    let key = get(values, `${primaryKeyAttr}.value`, get(values, primaryKeyAttr));

    if (!key) {
        return defaultKey;
    }

    if (isString(key)) {
        return key.length ? key : defaultKey;
    }

    return key;
}

// TODO: use mini-javascript parser and not eval for security reasons, possible XSS
export function evalVisibleConditions({ visibleConditions, editFieldValues, editFieldOptions, itemId, view, ...other }) {

    for (let sourceFieldName in visibleConditions) {
        if (!visibleConditions.hasOwnProperty(sourceFieldName)) continue;

        let condition = visibleConditions[sourceFieldName];

        if (!condition) {
            continue;
        }

        let value = editFieldValues[sourceFieldName],
            options = find(editFieldOptions[sourceFieldName], { value }),
            creating = itemId === identify.NEW_ITEM;

        try {
            /**
             *
             * DO NOT REMOVE (values, value, options, creating, view)
             * visibleConditions will stop to work if anything is removed.
             *
             */
            let result = (function (values, value, options, creating, view) {
                return !!eval(condition);
            })(editFieldValues, value, options, creating, view);

            // console.log('visibleCondition '+field.name+' ', { values, value, options, creating });
            // console.log('visibleCondition '+field.name+' | '+sourceFieldName+' "'+condition+'" => ', result);

            if (!result) {
                return false;
            }
        } catch (err) {
            console.error('Error while evaluating condition for ' + sourceFieldName + ' "' + condition + '"', err);
            return false;
        }
    }

    return true;
}

export function evalDisableConditions({ disableConditions, editFieldValues, editFieldOptions, itemId, view, ...other }) {
    if (isEmpty(disableConditions)) return false;
    return evalVisibleConditions({ editFieldValues, editFieldOptions, itemId, view, ...other, visibleConditions: disableConditions, })
}

export function prepareValueForSave(field, value) {
    if (!value) {
        return false;
    }

    const prepareSingleValue = (value) => {
        switch (field.type) {
            case 'date':
                try {
                    if (!value) {
                        return null;
                    }
                    value = replaceall('_', '', value);

                    return deFormatDate(parseGermanDate(value));
                } catch (e) {
                    return null;
                }
            case 'datetime':
                try {
                    if (!value) {
                        return null;
                    }

                    value = replaceall('_', '', value);

                    return deFormatDateTime(parseGermanDate(value));
                } catch (e) {
                    return null;
                }
        }

        return value;
    };

    if (typeof value === 'object') {
        if ('min' in value) {
            return {
                min: prepareSingleValue(value.min),
                max: prepareSingleValue(value.max),
            };
        }

        if ('value' in value) {
            return prepareSingleValue(value.value);
        }
    }

    return prepareSingleValue(value);
}

export function presentFilterValuesForQuery(filters, values) {
    return mapValues(values, (value, name) => {
        let filter = filters[name];
        return prepareValueForSave(filter, value);
    });
}

export function isModelViewValid(view) {
    return view && view !== identify.VIEW_SUBVIEW;
}

export function isItemView(view) {
    return view === identify.VIEW_VIEW || view === identify.VIEW_EDIT;
}

export function isTabularView(view) {
    return view === identify.VIEW_TABULAR;
}

export function presentColumnFields({ columns, itemRow }) {
    return reduce(
        columns,
        (columnFields, column) => {
            if (column.visible && column.fieldGroup) {
                columnFields[column.name] = {
                    name: 'field_' + column.name,
                    label: column.title,
                    value: itemRow[column.name].rendered,
                    group: column.fieldGroup,
                    visible: true,
                    tooltip: column.tooltip,
                    order: column.order,
                    renderAsHtml: true,
                    interval: column.interval,
                    timerDueText: column.timerDueText,
                };

                return columnFields;
            }

            return columnFields;
        },
        {}
    );
}

/**
 *
 * @param {{
 *  editFieldValues: any,
 *  editFields: any,
 *  itemId: any,
 *  parentSubview: any,
 *  editFieldOptions: any,
 *  view: any
 * }} props
 * @param {{ visible: boolean, nonew: boolean, subview: boolean, condition: boolean, onlyPersisted: boolean }} checks
 * @returns
 */
export function presentVisibleEditFields(
    props,
    checks = { visible: true, nonew: true, subview: true, condition: true, onlyPersisted: false, reference: false }
) {
    props = prepareEditFieldsValues(props);

    // inject those values into the edit field for future use (for example saving)
    props.editFields = mapValues(props.editFields, (editField, fieldName) => {
        return {
            ...editField,
            value: props.editFieldValues[fieldName],
        };
    });

    let visibleEditFields = reduce(
        props.editFields,
        (currentVisibleFields, field, fieldName) => {
            const checksCondition = checks.condition
                && (!checks.reference || !props.referenceFields?.includes(fieldName));

            if (
                (checks.visible && !field.visible) ||
                (checks.nonew && props.itemId === identify.NEW_ITEM && !field.editable) ||
                (checks.subview && props.parentSubview && props.parentSubview.hiddenFields.indexOf(fieldName) > -1) ||
                (checksCondition && !evalVisibleConditions({ visibleConditions: field.visibleConditions, ...props })) ||
                (checks.onlyPersisted && field.notPersisted) ||
                (props.controlSources && !props.controlSources.includes(field.group))
            ) {
                return currentVisibleFields;
            }

            currentVisibleFields[fieldName] = field;
            return currentVisibleFields;
        },
        {}
    );

    return visibleEditFields;
}

export function presentAutoCompleteFields(props) {
    const autocompleteEditFields = presentVisibleEditFields(props, { visible: false });
    return reduce(autocompleteEditFields, (autocompleteFields, field, fieldName) => {
        if (!field.editable) return autocompleteFields;
        if (field.isAutocomplete !== true) return autocompleteFields;
        autocompleteFields[fieldName] = field;
        return autocompleteFields
    }, {})
}

/**
 *
 * @param {{
*  editFieldValues: any,
*  editFields: any,
*  itemId: any,
*  parentSubview: any,
*  editFieldOptions: any,
*  view: any
* }} props
*/
const prepareEditFieldsValues = (props) => {
    let { editFields } = props;
    let fieldsToAdapt = pick(props.editFields, keys(props.editFieldValues));

    props = { ...props, ...normalizeValueDependencies(props) };
    props.editFieldValues = prepareFieldValues(fieldsToAdapt, props);
    props.editFieldValues = mapValues(props.editFieldValues, (val, fieldName) => {
        return prepareValueForSave(editFields[fieldName], val);
    });

    return props
}


function presentNestedVisibleGrid({
    grid, ...props
}) {
    if (grid.rows) {
        grid = {...grid};
        grid.rows = reduce(grid.rows, (rows, row, rowName) => {
            if (!isEmpty(row.visibleConditions) && !evalVisibleConditions({ visibleConditions: row.visibleConditions, ...props })) return rows;

            if (row.type === "row" && !isEmpty(row.cols)) {
                row = {
                    ...row,
                    cols: presentNestedVisibleGrid({
                        ...props,
                        grid: row,
                    }).cols,
                }
            };


            if (row.type === "widget") {
                const nodes = presentNestedVisibleGrid({
                    ...props,
                    grid: row,
                }).nodes;

                if (isEmpty(nodes)) return rows;

                row = {
                    ...row,
                    nodes,
                }
            };

            rows[rowName] = row;
            return rows;
        }, {});
    }


    if (grid.cols) {
        grid = {...grid};
        grid.cols = reduce(grid.cols, (cols, col) => {
            if (!isEmpty(col.visibleConditions) && !evalVisibleConditions({ visibleConditions: col.visibleConditions, ...props })) return cols;

            if (!isEmpty(col.rows)) {
                const rows = presentNestedVisibleGrid({
                    ...props,
                    grid: col,
                }).rows;


                if (isEmpty(rows)) return cols;

                col = {
                    ...col,
                    rows
                }
            };

            cols.push(col);
            return cols;
        }, []);
    }



    if (grid.nodes) {
        grid = {...grid};
        grid.nodes = reduce(grid.nodes, (nodes, tab) => {
            if (!isEmpty(tab.visibleConditions) && !evalVisibleConditions({ visibleConditions: tab.visibleConditions, ...props })) return nodes;

            const {rows: presentedRows, nodes: presentedNodes} = presentNestedVisibleGrid({
                ...props,
                grid: tab,
            });

            tab = {
                ...tab,
                disabled: tab.disabled || evalDisableConditions({ ...props, disableConditions: tab.disableConditions }),
                rows: tab.disabled ? [] : presentedRows,
                nodes: tab.disabled ? [] : presentedNodes,
            }


            if (isEmpty(tab.rows)) delete tab.rows;
            if (isEmpty(tab.nodes)) delete tab.nodes;

            if (tab.nodes || tab.rows) nodes.push(tab);

            return nodes;
        }, []);
    }

    return grid;
}

/**
 *
 * @param {{
*  editFieldValues: any,
*  editFields: any,
*  itemId: any,
*  parentSubview: any,
*  editFieldOptions: any,
*  view: any,
*  grid: any,
* }} props
* @param {{ visible: boolean, nonew: boolean, subview: boolean, condition: boolean, onlyPersisted: boolean }} checks
* @returns
*/
export function presentVisibleGrid(props) {
    props = prepareEditFieldsValues(props);
    return presentNestedVisibleGrid(props);
}


export function onlyPersisted(fieldDatas, editFields) {
    editFields = editFields ? editFields : fieldDatas;

    return reduce(
        fieldDatas,
        (theFieldDatas, fieldData, fieldName) => {
            if (editFields[fieldName].notPersisted) {
                return theFieldDatas;
            }

            theFieldDatas[fieldName] = fieldDatas[fieldName];
            return theFieldDatas;
        },
        {}
    );
}

export function dropIgnoredFields(editFields) {
    return {
        editFields: reduce(
            editFields,
            (fields, field, fieldName) => {
                if (field.ignored) {
                    return fields;
                }

                fields[fieldName] = editFields[fieldName];
                return fields;
            },
            {}
        ),
    };
}

export function presentSaveEditFields(props) {
    return presentVisibleEditFields(props, { visible: false, nonew: false, subview: false, condition: true, reference: true });
}

export function presentElements(elementTypes) {
    return mapValues(elementTypes, (elementType) => {
        return groupBy(elementType, 'group');
    });
}

export function normalizeAllFieldOptions(fields, allOptions) {
    try {
        return reduce(
            allOptions,
            (theOptions, options, name) => {
                let field = fields[name];

                theOptions[name] = field.isAutocomplete ? getOrFail(options, `autocomplete.value`) : field.options;

                return theOptions;
            },
            {}
        );
    } catch (err) {
        throw err;
    }
}

export function mergeField(props) {
    let { field, value, initialValue } = props;

    let fieldData = {
        ...field,
        initialValue,
    };

    if (!field.adaptValue) {
        return { ...fieldData, value };
    }

    props = { ...props, ...normalizeValueDependencies(props) };
    value = presentFieldValue(field, props);

    return { ...fieldData, value };
}

export function presentConstraintValues(inputType, constraints, props) {
    props = {
        ...props,
        ...normalizeValueDependencies(props),
    };

    let fields = inputType === identify.EDITFIELD ? props.editFields : props.filters;

    return mapValues(constraints, (v, fieldName) => {
        return presentFieldValue(fields[fieldName], props).value;
    });
}

export function presentItemTitle({ itemId, singleTitle, modelTitle, resultsValue, titleColumn }) {
    if (!itemId) {
        return modelTitle;
    }
    if (itemId === identify.NEW_ITEM) {
        return '+ ' + singleTitle;
    }

    let row = mapValues(get(resultsValue, `results.${itemId}`), (col) => col.rendered);

    if (!row || size(row) < 1) {
        return singleTitle;
    }

    if (!titleColumn || !titleColumn.length) {
        return get(row, 'title', singleTitle);
    }

    return template(titleColumn, row);
}

export function presentRawTitle(props) {
    return stripTags(presentItemTitle(props));
}

export function areValuesEqual(a, b) {
    const compareFoo = (a, b, key) => {
        let isEqual = isFieldValueEqual(a, b);
        return isEqual;
    };

    return compareKeys(a, b, { compareFoo });
}

export function presentRouterRoute(routerInfo) {
    return {
        pathname: routerInfo.pathname,
        query: routerInfo.query,
        options: routerInfo.options,
    };
}

function prepareActionGroup({ action, group }) {
    if ([identify.EDIT_ACTION, identify.SAVE_ACTION].includes(action.value)) {
        action = {...action};
        action.group = identify.PRIMARY_ACTION_GROUP;
    }

    if (typeof action.group === 'string') {
        return group ?? {
            name: action.group || `$$${action.value}__group`,
            order: action.order ?? -1,
            icon: action.icon,
            iconFamily: action.iconFamily,
            title: action.caption,
            showLabel: action.showLabel,
            subgroup: identify.PRIMARY_ACTION_GROUP === action.group ? action.subgroup || action.group : undefined,
            flatten: true,
        }
    }

    return {
        order: 1000,
        name: identify.DEFAULT_ACTION_GROUP,
        icon: 'more-vert',
    }
}


function prepareActionSubgroup({ action, group }) {
    if (!action.subgroup) {
        return {
            order: -1,
            name: identify.DEFAULT_ACTION_GROUP,
        }
    }

    return group ?? {
        name: action.subgroup,
        order: 0,
    }

}

export function presentActionInfo({
    groups,
    selectedRows,
    actions,
    actionResult = null,
    executeAction = null,
    executeScanQrAction = null,
}) {

    actions = actions.map((theAction) => {
        const group = prepareActionGroup({ action: theAction, group: get(groups, theAction.group) })

        return {
            ...theAction,
            order: theAction.order ?? -1,
            action: theAction.action ?? (theAction.scanQr ? executeScanQrAction : executeAction),
            group,
            subgroup: prepareActionSubgroup({action: theAction, group: get(groups, theAction.group) }),
            isActive: actionResult
                ? actionResult.actionName === theAction.value && actionResult.status === identify.STATUS_STARTED
                : false,
            requiresSelection: theAction.requires_selection && selectedRows.length === 0,
            criticalConfirmQuantity:
                theAction.criticalConfirmQuantity !== true && theAction.criticalConfirmQuantity
                    ? theAction.criticalConfirmQuantity
                    : 0,
            criticalConfirmText:
                theAction.criticalConfirmText ||
                (theAction.criticalConfirmQuantity ? theAction.caption.toUpperCase().replace(/_/g, ' ') : ''),
            disabled: !theAction.hasPermission,
            closeOn: !!theAction.grid ? "success" : "submit",
            tags: !!theAction.grid ? "grid-action" : undefined,
            isPrimary: theAction.group === identify.PRIMARY_ACTION_GROUP,
        };
    });

    const activeAction = actionResult ? find(actions, { isActive: true }) : null,
        activeGroup = activeAction?.group,
        isActionActive = !!activeAction;

    actions = orderBy(actions, ['group.order', "group.subgroup.order", 'order'], ['asc', 'asc', 'asc']);

    return { actions: groupBy(actions, 'group.name'), activeAction, activeGroup, isActionActive };
}

function presentDeleteAction({actions, itemId, groups, trans, actionPermissions, deleteTooltip, deleteItem}) {
    actions = [...actions];

    const deleteAction = actions.find(action => identify.DELETE_ACTION === action.value);
    const moreActionsGroup = get(groups, identify.MORE_ACTIONS_GROUP, { name: "" });

    const preparedDeleteAction = {
        // icon: 'delete',
        group: moreActionsGroup?.name,
        value: identify.DELETE_ACTION,
        icon: 'delete-forever',
        caption: trans('remove'),
        tooltip: deleteTooltip,
        ...deleteAction,
        confirmation: true,
        hasPermission: itemId !== identify.NEW_ITEM && get(actionPermissions, 'delete', false),
        action: deleteItem,
    };

    const deleteActionIndex = actions.findIndex(action => deleteAction === action);

    if (deleteActionIndex < 0) actions.push(preparedDeleteAction);
    else actions.splice(deleteActionIndex, 1, preparedDeleteAction);

    return actions;
}

function presentPrimaryActions({view, actions, itemId, trans, actionPermissions, saveItem, editItem, isDirty, subviewCount }) {
    const isNewItem = itemId === identify.NEW_ITEM;

    const editAction = actions.find(action => identify.EDIT_ACTION === action.value);
    const saveAction = actions.find(action => identify.SAVE_ACTION === action.value) || (isNewItem ? {} : undefined);

    if (!saveAction && !editAction) return actions;

    actions = [...actions];

    if (editAction) {
        actions.splice(actions.indexOf(editAction), 1);
    }

    if (saveAction) {
        const showContinue = subviewCount && isNewItem;
        const canSave = isNewItem ? actionPermissions.create : actionPermissions.update;

        isDirty = isDirty || !!saveAction.enabled;
        const icon = !isNewItem && !isDirty ? 'done-all' : saveAction.icon || 'save';
        const iconFamily = !isNewItem && !isDirty ? undefined : saveAction.icon ? saveAction.iconFamily : undefined;

        actions.splice(actions.indexOf(saveAction), 1, {
            value: identify.SAVE_ACTION,
            showLabel: true,
            allowDirty: isDirty || isNewItem,
            ...saveAction,
            variant: "solid",
            allowDisabled: canSave || isNewItem,
            group: saveAction.group || identify.PRIMARY_ACTION_GROUP,
            caption: showContinue ? trans('continue') : (saveAction.caption || trans('save')),
            icon: showContinue ? 'forward' : icon,
            iconFamily: showContinue ? undefined : iconFamily,
            hasPermission: isNewItem || (canSave && isDirty && saveAction.hasPermission),
            action: saveItem,
        });
    }

    return actions.filter(Boolean);
}

function presentItemActions({actions, itemId}) {
    if (itemId === identify.NEW_ITEM) {
        return actions.filter(action => action.group === identify.PRIMARY_ACTION_GROUP);
    }

    return actions;
}

export function presentItemActionInfo({
    groups,
    actions,
    itemId,
    saveResult,
    deleteResult,
    deleteItem = null,
    openItemLink = null,
    executeAction = null,
    executeScanQrAction = null,
    actionPermissions = {},
    trans,
    itemTitle,
    deleteTooltip,
    selectedRows = [],
    resultsValue,
    saveItem,
    editItem,
    isDirty,
    view,
    subviewCount,
}) {
    actions = [...actions];

    let saving = saveResult.status === identify.STATUS_STARTED,
        deleting = deleteResult.status === identify.STATUS_STARTED;


    actions = presentItemActions({actions, itemId });
    actions = presentDeleteAction({actions, itemId, groups, trans, actionPermissions, deleteTooltip, deleteItem});
    actions = presentPrimaryActions({view, actions, itemId, groups, trans, actionPermissions, saveItem, editItem, isDirty, subviewCount});

    actions = presentActionInfo({ groups, actions, executeAction, executeScanQrAction, selectedRows }).actions;

    return {
        saving,
        deleting,
        actions,
    };
}

export function presentAllResultParams(props) {
    // console.log('presentAllResultParams', props);

    let resultParams = {
        modelName: props.modelName,
        filters: presentAppliedFilterValues(props),
        page: props.page || 1,
        sortOptions: presentSortOptions(props),
        perPage: props.isCalendarEnabled ? props.perPage : Math.min(props.perPage, getConfig().maxPerPage),
        columns: presentColumns(props)
            .filter((c) => !c.hidden)
            .map(({ name }) => name)
            .sort()
            .join(','),
    };

    return resultParams;
}

export function presentAuthError(authResult) {
    // TODO?

    if (authResult.value) {
        return false;
    }
    if (authResult.started_at && authResult.value === null) {
        return false;
    }
    return authResult.error;
}

export function presentSubviewFilterValues({ parentPrimaryKey, parentItemId, parentItemValues, parentSubview }) {
    if (!parentSubview || !parentItemValues || !parentItemId) {
        return {};
    }

    let filterValue = null;
    if (parentSubview.filterValue === parentPrimaryKey) {
        filterValue = { value: parentItemId };
    } else {
        filterValue = parentItemValues[parentSubview.filterValue];
    }

    return { [parentSubview.filterName]: filterValue };
}

export function presentResultDependenciesLoaded(props) {
    let { subviewName, parentSubview, parentItemState, parentItemId, parentPrimaryKey } = props;
    if (!subviewName || !subviewName.length) {
        return true;
    }

    if (parentItemId === identify.NEW_ITEM || !parentItemId) {
        return false;
    }

    if (parentSubview.filterValue === parentPrimaryKey) {
        return true;
    }

    if (!parentItemState.value.itemId) {
        return false;
    }

    return parentItemState.value.itemId === parentItemId;
}

export function presentResultsLoading(props) {
    if (!presentResultDependenciesLoaded(props)) {
        return true;
    }

    let { resultState } = props,
        loading = resultState.status === identify.STATUS_STARTED;

    if (loading) {
        return true;
    }

    let resultParams = presentAllResultParams(props),
        loadedResultParams = get(resultState, 'loadedParams');

    let parametersNotEqual = !isEqual(resultParams, loadedResultParams);

    return parametersNotEqual;
}

export function presentSubviewIdentifier({ subview, subviews }) {
    let count = reduce(
        subviews,
        (theCount, subviewCandidate) => {
            return subviewCandidate.model === subview.modelName ? theCount + 1 : theCount;
        },
        0
    );

    return count > 1 ? subview.name : subview.model;
}

export function presentModelIdentifier({
    loadedModels,
    currentModelId,
    subviews,
    includeItemId = false,
}) {
    let slug = '';

    let currentModel = get(loadedModels, currentModelId);
    if (!currentModel.subviewName) {
        slug = `${slug}/${currentModel.modelName}`;
    } else {
        slug = `${slug}/${presentSubviewIdentifier({
            subview: getOrFail(subviews, currentModel.subviewName),
            subviews,
        })}`;
    }

    if (includeItemId && currentModel.item.id) {
        slug = `${slug}/${currentModel.item.id}`;
    }

    return slug;
}

export function presentModelUrl({
    loadedModels,
    modelId,
    models,
    includeCurrent = false,
    includeItemId = false,
}) {
    let nameSlug = '';
    let initialModelId = modelId;

    while (modelId) {
        let currentModel = get(loadedModels, modelId),
            currentModelId = modelId;

        modelId = currentModel.parentModelId;

        // TODO idk, this is wrong somehow, rename one thing means happy refactoring
        let modelName = get(loadedModels, `${modelId}.modelName`, null);
        let subviews = get(models, `${modelName}.dataModel.item.subviews`, {});

        let modelIdentifier = presentModelIdentifier({ loadedModels, currentModelId, subviews, includeItemId: true });
        if (currentModelId === initialModelId) {
            modelIdentifier = presentModelIdentifier({
                loadedModels,
                currentModelId,
                subviews,
                includeItemId,
            });
        }

        if (currentModelId !== initialModelId || includeCurrent) {
            nameSlug = nameSlug.length ? `${modelIdentifier}${nameSlug}` : modelIdentifier;
        }
    }

    return nameSlug;
}

export function normalizeValueDependencies({ emptyFilterValues, ...props }) {
    return {
        filterValues: merge({}, emptyFilterValues, presentFilterValues(props)),
        filterOptions: normalizeAllFieldOptions(props.filters, props.filterOptions),
        editFieldOptions: normalizeAllFieldOptions(props.editFields, props.editFieldOptions),
    };
}

function prepareEvalValues(props, type, valuePath = 'value') {
    const evalValues = {
        itemId: props.itemId,
        searchValue: props.searchValue,
        parent: props.parent && prepareEvalValues(props.parent, type),
        options: type === identify.EDITFIELD ? props.editFieldOptions : props.filterOptions,
        initialValues: mapValues(type === identify.EDITFIELD ? props.initialEditFieldValues : props.initialFilterValues, valuePath),
        values: mapValues(type === identify.EDITFIELD ? props.editFieldValues:  props.filterValues, valuePath),
    }

    return evalValues;
}


function findAdaptField(fieldName, inputType, props, isForeignField = false) {
    let parentField = null;
    if (!props) return parentField;


    const [, parentFieldName] = fieldName.startsWith("parent.") ? fieldName.split('.') : [fieldName];
    if (parentFieldName) return findAdaptField(parentFieldName, inputType, props.parent, true);

    parentField = inputType === identify.EDITFIELD
        ? props.editFields[fieldName]
        : props.filters[fieldName];

    if (!parentField) return null

    return {
        info: presentFieldValue(parentField, props),
        sourceField: parentField,
        isForeignField
    };

}

function prepareSourceFieldsInfo(referenceField, props) {
    const adaptInfo = referenceField.adaptValue;
    return uniq(adaptInfo.fields)
        .map((fieldName) => findAdaptField(fieldName, adaptInfo.inputType, props))
        .filter((field) => !!field?.info);
}

// TODO: support min/max for coverage even if I dont know how one can use that
// TODO: support for multiselect, needs isEqual function
export function presentFieldValue(field, props) {
    try {
        let {
            initialEditFieldValues,
            editFieldValues,
            filterValues,
            initialFilterValues,
            filterOptions,
            editFieldOptions,
        } = props;

        let adaptInfo = field.adaptValue;

        let fieldInputType = field.inputType,
            fieldValueInfo =
                fieldInputType === identify.EDITFIELD ? editFieldValues[field.name] : filterValues[field.name],
            initialFieldValueInfo =
                fieldInputType === identify.EDITFIELD
                    ? initialEditFieldValues[field.name]
                    : initialFilterValues[field.name];

        fieldValueInfo = fieldValueInfo ?? initialFieldValueInfo ?? { changed_at: 0, value: null, previousValue: null };

        if (!adaptInfo) {
            return fieldValueInfo;
        }

        // console.log('ADAPTING '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value);

        const sourceFields = prepareSourceFieldsInfo(field, props);

        const getAdaptValue = (valuePath = 'value') => {
            if (adaptInfo.eval) {
                const evaluateValue = new Function("values", "initialValues", "options", "parent", "searchValue", "value", adaptInfo.eval);
                const preparedEvalValues = prepareEvalValues(props, adaptInfo.inputType, valuePath);

                try {
                    return normalizeFieldValue(
                        field.inputType, field.type,
                        evaluateValue(
                            preparedEvalValues.values,
                            preparedEvalValues.initialValues,
                            preparedEvalValues.options,
                            preparedEvalValues.parent,
                            preparedEvalValues.searchValue,
                            get(fieldValueInfo, valuePath, initialFieldValueInfo.value),
                        )
                    )
                } catch (err) {
                    return normalizeFieldValue(field.inputType, field.type, null)
                }
            }

            // recursively resolve source value
            let {info: sourceValueInfo, sourceField} = sourceFields[0];
            let value = sourceValueInfo[valuePath];

            if (!adaptInfo.option) {
                return normalizeFieldValue(field.inputType, field.type, value);
            }

            let allFieldOptions =
                    adaptInfo.inputType === identify.EDITFIELD
                        ? editFieldOptions[sourceField.name]
                        : filterOptions[sourceField.name],
                options = find(allFieldOptions, { value });

            // as we do not know the format of the option, we need to format it according to the fields expectation
            return normalizeFieldValue(
                field.inputType,
                field.type,
                get(options, adaptInfo.option, initialFieldValueInfo.value)
            );
        };


        // if user input in that field is more recent than a change in the source input the user input takes precedence
        const sourceFieldsUpdated = !sourceFields.length ||
            sourceFields.every(({info, isForeignField}) => {
                const isFieldMoreRecent = fieldValueInfo.changed_at > info.changed_at;

                if (isForeignField) {
                    return isFieldMoreRecent && (
                        fieldValueInfo.previousValue !== undefined
                        || !isFieldValueEqual(fieldValueInfo, initialFieldValueInfo)
                    )
                };


                if (fieldValueInfo.changed_at === info.changed_at && fieldHasValue(fieldValueInfo)) return true;
                    
                // if (!isFieldMoreRecent && fieldHasValue(fieldValueInfo)) return true;
                return isFieldMoreRecent || isFieldValueEqual(fieldValueInfo, info);
            })

        if (sourceFieldsUpdated) {
            return fieldValueInfo;
        }

        // if source was changed more recently and mode is overwrite we want the source value
        if (adaptInfo.mode === identify.VALUE_ADAPT_OVERWRITE) {
            // console.log('ADAPT overriding '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value+' => '+getAdaptValue());

            return { ...fieldValueInfo, value: getAdaptValue() };
        }

        // we assume that we are in substitution mode from now on

        // if the field still has the initial value we want the source value
        if (initialFieldValueInfo.value === fieldValueInfo.value) {
            // console.log('ADAPT substitute if the field still has the initial value we want the source value '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value+' => '+getAdaptValue());

            return { ...fieldValueInfo, value: getAdaptValue() };
        }

        // if the field is equal to the previous adapted value we want the source value as well
        if (fieldValueInfo.value === getAdaptValue('previousValue')) {
            // console.log('ADAPT substitute if the field is equal to the previous adapted value we want the source value as well '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value+' => '+getAdaptValue());

            return { ...fieldValueInfo, value: getAdaptValue() };
        }

        // console.log('ADAPT original value '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value+' => '+getAdaptValue());

        // otherwise return the original field value
        return fieldValueInfo;
    } catch (err) {
        throw err;
    }
}

export function prepareFieldValues(fieldsToPrepare, props) {
    return mapValues(fieldsToPrepare, (field) => presentFieldValue(field, props));
}

export function prepareComparableOneFieldValue (field, preparedField) {
    if (!Array.isArray(preparedField.value)) return preparedField;
    if (field.orderable) return preparedField;
    return {
        ...preparedField,
        value: sortBy(preparedField.value),
    };
}

export const prepareComparableFieldValues = (fieldsToPrepare, props) => {
    return mapValues(fieldsToPrepare, (field) => {
        const preparedField = presentFieldValue(field, props);
        return prepareComparableOneFieldValue(field, preparedField);
    });
};

// TODO is this necceccarry? right now not used, fix constraints, bookings errors due to no values
export function getSourceFieldNames(fields, editFields, filters, { collectNames = true, makeUniq = true }) {
    fields = arrayed(fields);

    let sourceFieldNames = {
        [identify.EDITFIELD]: [],
        [identify.FILTERFIELD]: [],
    };

    let typedSourceFields = {
        [identify.EDITFIELD]: {},
        [identify.FILTERFIELD]: {},
    };

    const prepareSourceFieldNames = () => {
        let preparedSourceFieldnames = sourceFieldNames;

        if (makeUniq) {
            preparedSourceFieldnames = {
                [identify.EDITFIELD]: uniq(sourceFieldNames[identify.EDITFIELD]),
                [identify.FILTERFIELD]: uniq(sourceFieldNames[identify.FILTERFIELD]),
            };
        }

        return preparedSourceFieldnames;
    };

    forOwn(fields, (field) => {
        if (collectNames) {
            sourceFieldNames[field.inputType].push(field.name);
        }

        if (field.constraints) {
            let fields = field.inputType === identify.EDITFIELD ? editFields : filters;

            for (let key in field.constraints) {
                if (!field.constraints.hasOwnProperty(key)) {
                    continue;
                }

                let sourceField = fields[key];
                if (!sourceField) {
                    continue;
                }

                typedSourceFields[field.inputType][key] = sourceField;
            }
        }

        if (field.adaptValue) {
            field.adaptValue.fields.forEach((sourceFieldName) => {
                let sourceField =
                    field.adaptValue.inputType === identify.EDITFIELD
                        ? editFields[sourceFieldName]
                        : filters[sourceFieldName];

                if (sourceField) {
                    typedSourceFields[sourceField.inputType][sourceField.name] = sourceField;
                }
            })
        }
    });

    let sourceFields = [
        ...values(typedSourceFields[identify.EDITFIELD]),
        ...values(typedSourceFields[identify.FILTERFIELD]),
    ];

    if (!sourceFields.length) {
        return prepareSourceFieldNames(sourceFieldNames);
    }

    let nestedSourceNames = getSourceFieldNames(sourceFields, editFields, filters, {
        collectNames: true,
        makeUniq: false,
    });

    sourceFieldNames = {
        [identify.EDITFIELD]: [...sourceFieldNames[identify.EDITFIELD], ...nestedSourceNames[identify.EDITFIELD]],
        [identify.FILTERFIELD]: [...sourceFieldNames[identify.FILTERFIELD], ...nestedSourceNames[identify.FILTERFIELD]],
    };

    return prepareSourceFieldNames(sourceFieldNames);
}

// export function reduceFieldSources(fields, editFields, filters, sources) {
//
// }

export function reduceFieldSources(fields, editFields, filters, sources) {
    let sourceFieldNames = getSourceFieldNames(fields, editFields, filters, sources);

    let editFieldSources = mapValues(
        pick(sources, ['initialEditFieldValues', 'editFieldValues', 'editFieldOptions']),
        (source) => {
            return pick(source, sourceFieldNames[identify.EDITFIELD]);
        }
    );

    let filterSources = mapValues(
        pick(sources, ['filterValues', 'initialFilterValues', 'emptyFilterValues', 'filterOptions']),
        (source) => {
            return pick(source, sourceFieldNames[identify.FILTERFIELD]);
        }
    );

    const parentFields = sources.parent && arrayed(fields).reduce((all, field) => {
        if (!field.adaptValue?.fields) return all;

        const fields = field.adaptValue.inputType === identify.EDITFIELD
            ? sources.parent.editFields
            : sources.parent.filters;

        const resolvedParentFields = field.adaptValue.fields.map((adaptValueFieldName) => {
            const [, fieldName] = adaptValueFieldName.split('.');
            if (!fieldName) return null;

            return fields[fieldName];
        }).filter(Boolean);

        return [...all, ...resolvedParentFields];
    }, [])

    return {
        ...editFieldSources,
        ...filterSources,
        parent: !parentFields?.length ? null : sources.parent,
    };
}

export function presentSimpleEntry({ entry, resultInfo }) {
    return {
        title: stripTags(get(entry, `${resultInfo.titleColumn}.rendered`, null)),
        body: stripTags(get(entry, `${resultInfo.bodyColumn}.rendered`, null)),
        info: stripTags(get(entry, `${resultInfo.infoColumn}.rendered`, null)),
    };
}

export function presentSimpleResults({ results, resultInfo }) {
    return mapValues(results, (entry) => {
        return presentSimpleEntry({ entry, resultInfo });
    });
}

export function presentUpdatedValues({ startValues, values }) {
    return reduce(
        values,
        (theUpdatedValues, value, name) => {
            let startValue = startValues[name];

            if (isFieldValueEqual(startValue, value)) {
                return theUpdatedValues;
            }

            theUpdatedValues[name] = value;

            return theUpdatedValues;
        },
        {}
    );
}

export function haveValuesChanged(props) {
    let updatedValues = presentUpdatedValues(props);

    let changedValueCount = size(updatedValues);

    return changedValueCount > 0;
}

function presentReferenceFields(state, modelId, gridName) {
    if (!gridName) return [];

    const fieldGrid = getFieldGrid(state, modelId, gridName);
    function collectReferenceFields(grid) {
        const rows = [].concat(grid);

        return reduce(rows, (fields, row) => {
            if (row.type === 'widget') {
                fields.push(row.referenceField)
                fields.push(...collectReferenceFields(Object.values(row.nodes ?? {})));

                return fields;
            }

            fields.push(...collectReferenceFields(Object.values(row.rows ?? {})));
            fields.push(...collectReferenceFields(Object.values(row.cols ?? {})));

            return fields;
        }, []);

    }

    return collectReferenceFields(fieldGrid)
}

export function getSaveValues(state, modelId, gridName) {
    let props = {
            ...getValueAdaptData(state, modelId),
            ...getValueAdaptSources(state, modelId),
            referenceFields: presentReferenceFields(state, modelId, gridName),
        },
        visibleEditFields = presentSaveEditFields(props);

    return mapValues(visibleEditFields, 'value');
}

export function getResultQueryParams(state, modelId) {
    let rawResultParams = getAllResultParams(state, modelId),
        filters = getFilters(state, modelId),
        resultParams = presentAllResultParams(rawResultParams);

    return {
        ...resultParams,
        filters: presentFilterValuesForQuery(filters, resultParams.filters),
    };
}

export function getResultQueryParamsWithSelectedRows(state, modelId) {
    const queryParams = getResultQueryParams(state, modelId);
    const ids = getSelectedRows(state, modelId);
    const selectThroughout = getSelectThroughout(state, modelId);
    return {
        ...queryParams,
        filters: {
            ...queryParams.filters,
            _ids: !selectThroughout && ids.length ? ids : undefined,
            _without_ids: selectThroughout && ids.length ? ids : undefined,
        },
    };
}

export function getModelsWithChangedData(state) {
    let loadedModels = state.ui.loadedModels;

    return reduce(
        loadedModels,
        (um, model, modelName) => {
            if (!get(model, 'item.id') || !get(model, 'itemData.finished_at')) {
                return um;
            }

            let modelId = model.modelId;

            let valueAdaptProps = {
                ...getValueAdaptData(state, modelId),
                ...getValueAdaptSources(state, modelId),
            };

            valueAdaptProps = { ...valueAdaptProps, ...normalizeValueDependencies(valueAdaptProps) };
            valueAdaptProps = { ...valueAdaptProps, ...dropIgnoredFields(onlyPersisted(valueAdaptProps.editFields)) };

            let itemData = model.itemData.value;
            let values = prepareComparableFieldValues(valueAdaptProps.editFields, valueAdaptProps),
                startValues = prepareComparableFieldValues(valueAdaptProps.editFields, {
                    ...valueAdaptProps,
                    initialEditFieldValues: itemData.initialValues,
                    editFieldValues: itemData.values,
                });

            let updatedValues = presentUpdatedValues({ values, startValues });

            if (size(updatedValues) > 0) {
                um[modelName] = {
                    updatedValues,
                    model,
                };
            }

            return um;
        },
        {}
    );
}

export function presentHelpTitle(entry, defaultStr) {
    return entry ? entry.title : defaultStr;
}

export function presentSearchResultParams({ searchModel, searchTerm, searchSort, searchFilter }) {
    try {
        let resultParams = {
            filters: {
                [searchFilter]: searchTerm,
            },
            modelName: searchModel.modelName,
            page: searchModel.results.value.page || 1,
            sortOptions: searchSort,
            perPage: searchModel.resultParams.perPage,
        };

        return resultParams;
    } catch (err) {
        console.log('yeah');
    }
}

export function haveSearchModelsErrored(searchModels) {
    let findResult = find(searchModels, (model) => {
        return model.results.status === identify.STATUS_ERRORED;
    });

    return !!findResult;
}

export function presentFieldFile({ publicPath, name, modelName, itemId = null, value = null }) {
    let baseUrl = getConfig().basePublicUrl;

    if (publicPath) {
        return `${baseUrl}/${publicPath}/${value}`;
    }

    return fieldFile(modelName, itemId, name);
}

export function presentFieldImage(props, resizeProps) {
    let { publicPath, name, modelName, itemId = null, value = null } = props;

    if (publicPath) {
        return timthumb(`${publicPath}/${value}`, resizeProps);
    }

    return fieldFile(modelName, itemId, name);
}

export function deriveNewListOrder(list, oldIndex, newIndex) {
    const columnOrder = list.map(({ displayOrder }) => displayOrder);
    const [leftOrder = 0, newOrder = leftOrder + 1, rightOrder = newOrder + 1] = [
        columnOrder[newIndex - 1],
        columnOrder[newIndex],
        columnOrder[newIndex + 1],
    ];

    const column = list[oldIndex];
    let orderDifference =
        oldIndex > newIndex
            ? // when moving down, subtract difference from newOrder
              (leftOrder - newOrder) / 2
            : // when moving up, add difference to newOrder
              (rightOrder - newOrder) / 2;

    return { [column.name]: newOrder + orderDifference };
}

export function presentRouteViews(state) {
    const base64View = getRouter(state).params.viewId;
    if (!base64View) return null;

    try {
        return JSON.parse(decode(base64View));
    } catch (e) {
        return null;
    }
}

export function presentFilteredViewsWithRelativeDates(view) {
    return omitBy(
        {
            ...view,
            filters:
                view.filters &&
                mapValues(view.filters, ({ value, ...filter }) => ({
                    ...filter,
                    value: !isDateFilter(filter.filterType)
                        ? value
                        : getFilterType('date')
                              .find(({ name }) => name === filter.filterType)
                              .getValue(),
                })),
        },
        (x) => x === undefined
    );
}

export function presentActiveRelationshipFieldTree(state) {
    let tree = [];
    const modelTree = getModelTree(state);
    let rootModelId = getCurrentModelId(state);
    const filterTree = () => Object.fromEntries(map(orderBy(tree, "activated_at", "asc"), (item) => [item.modelId, item.hasSubviews]));
    const allModelIds = [
        rootModelId,
        ...modelTree
            .getAllChildren(rootModelId)
            .filter((model) => !!getActiveRelationshipField(state, model.modelId))
            .map(model => model.modelId)
    ];

    while(true) {
        if (!allModelIds.length) return filterTree();

        const currentModelId = allModelIds.shift();

        try {
            const activeRelationshipField = getActiveRelationshipField(state, currentModelId);
            if (!activeRelationshipField) continue;

            const nextModelId = find(modelTree.getChildren(currentModelId), { modelName: activeRelationshipField.model, hasForeignFieldRelation: true })?.modelId;
            allModelIds.push(
                ...modelTree.getChildren(nextModelId)
                    .filter((model) => !!getActiveRelationshipField(state, model.modelId))
                    .map((model) => model.modelId)
                    .filter((modelId) => !allModelIds.includes(modelId))
                );

            tree.push({ modelId: nextModelId, ...activeRelationshipField, hasSubviews: getSubviewCount(state, currentModelId) > 0 });
        }
        catch (err) {
            continue;
        }
    }
}


export function presentModelManagerProps(state, modelId) {
    const model = getModelTree(state).getModel(modelId);

    const hasDataModelLoaded = isDataModelLoaded(state, modelId);
    modelId = getModelNameOrNull(state, modelId) && modelId;

    if (!model.hasForeignFieldRelation) {
        return {
            isDataModelLoaded: hasDataModelLoaded,
            modelId
        }
    }

    const activeRelationshipField = getActiveRelationshipField(state, model.parentModelId);
    return {
        isDataModelLoaded: hasDataModelLoaded,
        modelId: activeRelationshipField?.model === model.modelName ? modelId : null
    }
}

export function presetActionPermissions(state, modelId, itemId) {
    const globalActionPermissions = getDataActionPermissions(state, modelId);
    if (!itemId) return globalActionPermissions;

    const itemActionPermissions = itemId ? getActionPermissions(state, modelId): globalActionPermissions;
    return mapValues({...globalActionPermissions, ...itemActionPermissions, }, (itemPermission = false, key) => {
        return !!Math.min(+(globalActionPermissions[key] || 0), +(itemPermission));
    });
}
